import { IconButton } from '@components/IconButton';
import { IconAdd, IconMail } from '@components/icons';
import { SocialIcon } from '@components/SocialIcon';
import { useCallback, useEffect, useRef } from 'react';
import styles from './ManageLeadsPage.module.scss';

export interface AddMenuProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  onAddContact: () => void;
  /** Whether to show the "Add WhatsApp" option. Defaults to true. */
  showWhatsApp?: boolean;
  onAddWhatsApp?: () => void;
}

/**
 * Dropdown menu for adding a contact (email) or a WhatsApp number.
 * Manages its own click-outside listener so each placement is self-contained.
 */
export const AddMenu = ({
  isOpen,
  onOpenChange,
  onAddContact,
  showWhatsApp = true,
  onAddWhatsApp,
}: AddMenuProps) => {
  const menuRef = useRef<HTMLDivElement>(null);

  const handleClickOutside = useCallback(
    (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        onOpenChange(false);
      }
    },
    [onOpenChange]
  );

  useEffect(() => {
    if (!isOpen) return;
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen, handleClickOutside]);

  return (
    <div className={styles.addMenuWrapper} ref={menuRef}>
      <IconButton
        size="small"
        aria-label="Add contact or WhatsApp"
        onClick={() => onOpenChange(!isOpen)}
      >
        <IconAdd size={16} />
      </IconButton>
      {isOpen && (
        <div className={styles.addMenu} role="menu">
          <button
            type="button"
            className={styles.addMenuItem}
            role="menuitem"
            onClick={() => {
              onOpenChange(false);
              onAddContact();
            }}
          >
            <IconMail size={18} />
            <span>Add Contact</span>
          </button>
          {showWhatsApp && (
            <button
              type="button"
              className={styles.addMenuItem}
              role="menuitem"
              onClick={() => {
                onOpenChange(false);
                onAddWhatsApp?.();
              }}
            >
              <SocialIcon provider="whatsapp" className={styles.addMenuWhatsappIcon} />
              <span>Add WhatsApp</span>
            </button>
          )}
        </div>
      )}
    </div>
  );
};
